import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { fetchDataSources, type ApiDataSource } from '../../lib/api'
import { PageHeader } from '../../components/layout/PageHeader'
import {
  Panel,
  Spinner,
  ErrorState,
  Table,
  type Column,
  MultiSelect,
  FilterBar,
  FilterSearch,
  FilterClear,
} from '../../components/ui'

function family(name: string): string {
  return name.split('_')[0]
}

export function DataLakePage() {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useQuery({ queryKey: ['data-sources'], queryFn: fetchDataSources })

  const [search, setSearch] = useState('')
  const [families, setFamilies] = useState<string[]>([])

  const all = useMemo(() => data ?? [], [data])
  const familyOptions = useMemo(
    () =>
      Array.from(new Set(all.map((d) => family(d.name))))
        .sort()
        .map((f) => ({ value: f, label: f.toUpperCase() })),
    [all],
  )

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return all.filter((d) => {
      if (families.length && !families.includes(family(d.name))) return false
      if (q && !d.name.toLowerCase().includes(q) && !d.table.toLowerCase().includes(q)) return false
      return true
    })
  }, [all, search, families])

  const hasFilters = search.trim() !== '' || families.length > 0

  const columns: Column<ApiDataSource>[] = [
    { key: 'name', header: 'Source', render: (s) => <span className="font-medium">{s.name}</span> },
    { key: 'table', header: 'Table', render: (s) => <span className="font-mono text-xs text-gray-500">{s.table}</span> },
    {
      key: 'rows',
      header: 'Rows',
      align: 'right',
      render: (s) => (s.row_count != null ? s.row_count.toLocaleString() : '—'),
    },
  ]

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-app-bg">
      <PageHeader crumbs={[{ label: 'Data Lake' }]} />
      <FilterBar>
        <FilterSearch value={search} onChange={setSearch} placeholder="Search source / table" />
        <MultiSelect label="Family" options={familyOptions} selected={families} onChange={setFamilies} />
        {hasFilters && (
          <FilterClear
            onClick={() => {
              setSearch('')
              setFamilies([])
            }}
          />
        )}
      </FilterBar>
      <div className="flex-1 overflow-auto p-4">
        <Panel>
          {isLoading ? (
            <Spinner />
          ) : isError ? (
            <ErrorState />
          ) : (
            <Table
              columns={columns}
              rows={rows}
              empty="No data sources."
              onRowClick={(s) => navigate({ to: '/data/$name', params: { name: s.name } })}
            />
          )}
        </Panel>
      </div>
    </div>
  )
}
